import { labelOptions } from "./consoleOptions";
import { makeLink } from "./links";
import { urls } from "./urls";
import { get } from "svelte/store";

// English
const minimumAge = 18;

type ErrorType = {
  title: string;
  subtitle: string;
  info?: string;
  retryButton?: string;
  renderActivisionErrorDetails?: boolean;
}

const copy = () => ({
  urls,
  prizeNames: {
    operatorSkin: "OPERATOR SKIN",
    operatorSkinSplit: "OPERATOR <br> SKIN",
    operatorSkinS: "Operator Skin",
    codPoints: "<i>CALL OF DUTY</i> &nbsp;POINTS",
    emblem: "EMBLEM",
    charm: "CHARM",
    callingCard: "CALLING CARD",
    plus: "Plus ",
    youCanWin: "YOU CAN WIN BATTLE PASSES & <i>CALL OF DUTY</i> &nbsp;POINTS!",
    youCanWinSplit: "YOU CAN WIN BATTLE PASSES <br> & <i>CALL OF DUTY</i> &nbsp;POINTS!",
    firstPurchase: "First Purchase:",
    secondPurchase: "Second Purchase:",
    thirdPurchase: "Third Purchase:",
    oneInGameEmblem: "(1) In-Game Emblem",
    oneInGameCharm: "(1) In-Game Charm",
    oneInGameCallingCard: "(1) In-Game Calling Card",
    oneInGameOperatorSkin: "(1) In-Game Operator Skin"
  },
  error: {
    generic: {
      title: "ERROR",
      subtitle: "An unknown error occurred.",
      retryButton: "Try again"
    } as ErrorType,
    invalidEntry: {
      title: "ERROR",
      subtitle: "THE CODE OR RECEIPT YOU ENTERED IS INVALID. PLEASE TRY AGAIN AND MAKE SURE YOU FOLLOW THE GUIDELINES.",
      info: `If you are still experiencing issues and believe your entry should be eligible, please contact ${makeLink(get(urls).contactUs, "Customer Support")}.`,
      retryButton: "Try again"
    } as ErrorType,
    unableToValidateReceipt: {
      title: "ERROR",
      subtitle: "WE WERE UNABLE TO VALIDATE YOUR ENTRY AT THIS TIME. PLEASE TRY AGAIN LATER.",
      retryButton: "Try again"
    } as ErrorType,
    unlucky: {
      title: "ERROR",
      subtitle: "We were unable to unlock your reward due to one of the following reasons:",
      renderActivisionErrorDetails: true,
      retryButton: "Try again"
    } as ErrorType,
    underage: {
      title: "ERROR",
      subtitle: `You must be at least ${minimumAge} years old to enter.`,
    } as ErrorType,
    alreadyParticipatedToday: {
      title: "ERROR",
      subtitle: "You have already submitted an entry today. Please come again tomorrow.",
    } as ErrorType,
    unsupportedCountry: {
      title: "ERROR",
      subtitle: "THIS CAMPAIGN IS NOT AVAILABLE IN YOUR COUNTRY.",
      info: `This promotion is only available to legal residents of the United States, Canada (excluding Quebec) and Mexico. Please check back for future promotions that may be available in your region.`,
    } as ErrorType,
    campaignNotSupported: {
      title: "ERROR", 
      subtitle: "THIS CAMPAIGN IS NOT SUPPORTED IN YOUR COUNTRY.",
      info: `Click below to visit the supported campaign.`,
      retryButton: "Participate"
    } as ErrorType
  },
  common: {
    unlock: "UNLOCK",
    dual2xp: "DUAL 2XP"
  },
  header: {
    menus: {
      orderFromLittleCaesars: "Order from Little Caesars<sup>®</sup>",
      chooseYourLanguage: "Choose your language",
    },
    ariaLabels: {
      logo: "Little Caesars logo",
      menuButton: "Open navigation menu"
    }
  },
  countrySelector: {
    title: "SELECT YOUR COUNTRY",
    placeholder: "Choose country",
    confirmButton: "CONFIRM"
  },
  teaser: {
    title: "REPORT BACK",
    subtitle: "THIS PROMOTION IS COMING SOON. <br> CHECK BACK NOVEMBER 3.",
  },
  over: {
    title: "MISSION <br> COMPLETE",
    subtitle: "THIS PROMOTION HAS ENDED."
  },
  prizes: {
    mobileTitleClasses: "text-[68px] mobileS:text-[82px] mobile:text-[110px] tablet:text-[84px]",
    title: "REWARDS & <br> PRIZES",
    subtitle: "SCOUT WHAT YOU COULD WIN!"
  },
  activisionErrorDetails: {
    reasons: [
      "You've reached the 40 hour Dual 2XP max in bonus content on your account. Once this status is reached, you're unable to receive any additional Dual 2XP tokens",
      "Your Activision account isn't linked to a gaming account. Please ensure it is to redeem the reward"
    ],
    contactSupport: `If the above options don't apply, please reach out to ${makeLink(get(urls).activisionSupport, "Activision Support")} for additional assistance.`
  },
  registration: {
    title: "IN-GAME REWARDS <br> ARE BACK",
    subtitle: "REGISTER NOW!",
    limitedToThreeTotalQualifyingPurchases: "(Limited to THREE TOTAL qualifying purchases)",
    requiredField: "Required Field",
    selectCountry: "Please select which country you live in:",
    consolePreference: "Gaming Console Preference for&nbsp;<i>Call of Duty: Black Ops 7</i>",
    consolePreferencePlaceholder: "Gaming Console Preference for Call of Duty: Black Ops 7",
    consolePreferenceOptions: labelOptions({
      xbox: "Xbox®",
      playstation: "PlayStation®",
      battleNet: "Battle.net",
      steam: "Steam®",
      microsoftStore: "Xbox® on PC"
    }),
    notGuaranteed: "Call of Duty: Black Ops 7 prize is not guaranteed.",
    firstNameLabel: "First Name",
    lastNameLabel: "Last Name",
    emailLabel: "Email Address",
    dobLabel: "Date of Birth",
    confirmEmailLabel: "Confirm Email Address",
    skillLabel: "Solve:",
    submitButton: "SUBMIT",
    minimumAge,
    ariaLabels: {
      checkboxDescribedBy: "checkbox switch",
      submitButton: "Submit registration form"
    },
    validations: {
      invalidSkillAnswer: "Invalid answer",
      invalidConsoleOption: "Please choose a gaming console preference",
      firstNameEmpty: "Please enter your first name",
      firstNameInvalid: "Your first name is too short",
      firstNameContainsIllegalCharacters: "Your first name contains illegal characters",
      lastNameEmpty: "Please enter your last name",
      lastNameInvalid: "Your last name is too short",
      lastNameContainsIllegalCharacters: "Your last name contains illegal characters",
      emailEmpty: "Please enter your email address",
      emailInvalid: "Please enter a valid email address",
      dobInvalid: "Please enter a valid date of birth",
      dobUnderage: `You must be at least ${minimumAge} years old to enter`,
      mandatoryCheckbox: "This checkbox is required to proceed"
    }
  },
  homepage: {
    titleTextClasses: "text-[80px] mobile:text-[120px] tablet:text-[136px] laptop:text-[150px] desktop:text-[170px] desktopLg:text-[214px]",
    for: "for",
    blackOps6: "Call of Duty® : Black Ops 7",
    note: `with any qualifying purchase<span class="reg">*</span> at Little Caesars<span class="reg">®</span>.`,
    bonus: "YOU CAN WIN in-game <br> rewards like Battle Passes <br> and <i>call of duty</i>&nbsp;&nbsp;points.",
    entry: {
      label: "YOUR EMAIL ADDRESS",
      button: "UNLOCK REWARDS",
      info: "Please enter the email address associated with your Activision account.",
      rules: `See ${makeLink(get(urls).officialRules, "Official Rules")} for how to enter without a purchase.`
    },
    rewardsButton: "SEE REWARDS"
  },
  purchaseSelection: {
    titleTextClasses: "text-[88px] mobileS:text-[100px] mobile:text-[120px] tablet:text-[84px] laptop:text-[100px] desktop:text-[110px] desktopLg:text-[140px]",
    subtitle: "SELECT HOW YOU'D LIKE TO ENTER YOUR PURCHASE",
    limits: "Maximum one code/receipt submission per day.",
    or: "Or",
    action: "ENTER OR UPLOAD",
    actionCodeSmall: "Enter",
    actionCode: "ENTER YOUR RECEIPT CODE",
    actionCodeDescription: "If you made a website, app or restaurant purchase and your receipt has a unique code, enter that code here.",
    actionUploadSmall: "Upload",
    actionUpload: "UPLOAD YOUR RESTAURANT RECEIPT",
    actionUploadDescription: "If you made a restaurant purchase and your printed receipt does not have a code, take a clear photo of the full receipt and upload it here.",
    checkRules: `See ${makeLink(get(urls).officialRules, "Official Rules")} for how to enter without a purchase.`
  },
  code: {
    title: `YOU’VE OBTAINED A CODE`,
    subtitle: "PLEASE ENTER THE UNIQUE CODE FROM YOUR LITTLE CAESARS® ORDER CONFIRMATION EMAIL OR RECEIPT.",
    invalidCode: "Invalid code",
    failedToSaveCode: "Failed to save code. Please try again.",
    label: "ENTER 7-DIGIT CODE",
    button: "SUBMIT",
    findCode: "Find your code in your online order confirmation email or on",
    findCodeLink: "your restaurant receipt",
    limits: "Maximum one code submission per day.",
    support: {
      haveCode: "Do you have a customer support code?",
      link: "Click here."
    },
    noCodeTryReceipt: {
      initial: "No code?",
      linked: "Click here to upload receipt"
    }
  },
  receipt: {
    titleTextClasses: "text-[50px] mobileS:text-[62px] mobile:text-[84px] tablet:text-[84px] laptop:text-[100px] desktop:text-[110px] desktopLg:text-[140px]",
    title: "SHARE YOUR INTEL",
    subtitle: `UPLOAD A PHOTO OF YOUR LITTLE CAESARS<span class="reg text-[0.7em] align-top">®</span> <br class="block mobile:hidden"> RESTAURANT RECEIPT HERE.`,
    noReceiptTryCode: {
      initial: "If you have a code on the bottom of your receipt,",
      linked: "click here to enter"
    },
    info: `Receipt uploads with codes will be rejected. <br class="block mobile:hidden"> Maximum one receipt submission per day.`,
    guidelinesTitle: "RECEIPT UPLOAD GUIDELINES",
    guidelineIntro1: "Please ensure you upload a clear photo of the",
    guidelineIntroLink: "full receipt",
    guidelineIntro2: "containing the following information:",
    guidelinesList: [
      "Unique ID (transaction number)",
      "Little Caesars location (must be Mexico or Canada, excluding Quebec)", // TODO: this needs to change for the international version
      "Purchase date (must fall between 11/03/25 and 12/31/25)",
      "Order subtotal (must meet qualifying purchase requirement*)",
      "Please include only one receipt per photo"
    ],
    uploadFromDevice: "UPLOAD FROM DEVICE",
    dragFileToUpload: "DRAG FILE TO UPLOAD",
    chooseFile: "CHOOSE FILE",
    continueButton: "NEXT",
    rejectedFile: "Your file was rejected by the server. Please make sure it is a valid JPEG or PNG file, that the file size is less than 6MB and that it follows the guidelines.",
    receiptData: {
      completeReceiptInfo: "Complete your receipt information",
      orderNumber: "Order Number",
      orderDate: "Order Date",
      location: "Location",
      locationPlaceholder: "Search store location",
      orderNumberPlaceholder: "Enter order number",
      submitReceipt: "Submit Receipt",
      validations: {
        orderNumberRequired: "Order number is required",
        orderNumberInvalid: "Order number should be a number",
        orderDateRequired: "Order date is required",
        orderDateTooRecent: "Order date is too recent. Please try again later.",
        orderDateInFuture: "Order date cannot be in the future",
        orderDateTooOld: "Receipts must be from after November 3rd",
        locationRequired: "Location is required"
      },
      codeGenerated: {
        message: `A code was generated for this receipt. Please check your email and use the code to redeem your prize. <br class="hidden tablet:block"> If you can't find it, please contact Little Caesars customer service at ${makeLink(get(urls).contactLittleCaesars, "Contact Us - Little Caesars®")}.`,
        linkText: "Click here to enter your code"
      },
      saveFailedErrorMessage: "Failed to save receipt information. Please try again."
    }
  },
  rateLimited: {
    title: "Rate Limited",
    message: "You have made too many attempts. Please wait a moment before trying again.",
    ariaCloseButton: "Close rate limited modal",
    ariaModalDescription: "Rate limited modal"
  },
  verifyModal: {
    closeButtonAriaLabel: "Close verification modal",
    modalAriaDescription: "Verify your phone number or email",
    title: "Verify your number",
    smsMessage: "Please enter the 6 digit code that was sent to your number ending in {phoneNumber}.",
    emailMessage: "Please enter the 6 digit code that was sent to your email.",
    toggleToEmail: "Prefer to verify by email?",
    toggleToSms: "Prefer to verify by text message?",
    toggleLink: "Click here",
    verifyButton: "Verify",
    countdownMessage: "Request new code in {countdown} seconds",
    requestNewCode: "Didn't receive a code? Click here to request a new one.",
    errors: {
      requestFailed: "Failed to request new verification code.",
      toggleChannelFailed: "Failed to toggle channel.",
      verificationFailed: "Verification failed. Please try again.",
      verificationFinishFailed: "Verification finish failed... {errorMessage}",
      multipleCallOfDutyIdentities: "The email you are trying to use is already linked to a different <i>Call of Duty</i> account."
    }
  },
  reviewReceipt: {
    titleTextClasses: "text-[50px] mobileS:text-[62px] mobile:text-[84px] tablet:text-[84px] laptop:text-[100px] desktop:text-[110px] desktopLg:text-[140px]",
    title: "REVIEW YOUR SHARED INTEL",
    subtitle: "CONFIRM THE UPLOAD OF YOUR RECEIPT.",
    flipPhoto: "Flip Photo",
    info: "Preview your entry submission. If you need to update anything or would like to submit a new entry, click BACK. Otherwise, click SUBMIT to complete your entry.",
    limits: "Maximum one receipt submission per day.",
    receiptPreview: "RECEIPT PREVIEW",
    backButton: "BACK",
    submitButton: "SUBMIT"
  },
  thankYou: {
    title: "WELCOME <br> ABOARD",
    subtitle: `THANKS FOR JOINING FORCES WITH US. <br class="hidden tablet:block"> YOU&rsquo;VE UNLOCKED:`,
    prizeInfo: "Please check your email for more information on your reward.",
    orderAgain: "ORDER AGAIN TOMORROW <br> AND UNLOCK MORE REWARDS!",
  },
  footer: {
    navItems: {
      rewardsAndPrizes: "Rewards & Prizes",
      officialRules: "Official Rules",
      faq: "FAQ",
      termsOfService: "Terms of Service",
      privacyPolicy: "Privacy Policy",
      usStatePrivacyPolicy: "US State Privacy Policy",
      contactUs: "Contact Us",
      nonParticipatingStores: "Non-Participating Stores"
    },
    ariaLabels: {
      navigation: {
        rewardsAndPrizes: "Rewards & Prizes opens in a new tab",
        officialRules: "Official Rules opens in a new tab",
        faq: "FAQ opens in a new tab",
        termsOfService: "Terms of Service opens in a new tab",
        privacyPolicy: "Privacy Policy opens in a new tab",
        usStatePrivacyPolicy: "US State Privacy Policy opens in a new tab",
        contactUs: "Contact Us opens in a new tab",
        nonParticipatingStores: "Non-Participating Stores opens in a new tab"
      }
    },
    info: `
      NO PURCHASE NECESSARY. Void Where Prohibited. Residents 50 US & DC, Canada (excl. Quebec), & Mexico, must be age of majority.
      Ends 12/31/25 (codes must be entered by 1/31/2026). 19,653 prizes avail. at start of promotion (ARV range: $4.99 - $99.99 USD).
      Limit 1HR of 2XP TOKEN per day; 40 HRS total. Applies to all 2XP promotions for <i>Call of Duty: Black Ops 7</i>
      (not limited to this promotion). Odds depend on entry time & numbers. Math skill test req'd in Canada and Mexico.
      ${makeLink(get(urls).officialRules, "Check the official rules")}. Sponsor: Little Caesar Enterprises, Inc.
    `,
    copyright: `
      ESRB rating: MATURE. Visit ${makeLink("https://www.esrb.org", "www.esrb.org")}. ©/™/® 2025 Activision Publishing, Inc.
    `,
    pattern: "/assets/images/esrb-en.svg?v=202511041232"
  }
});

export default copy;
